/**
 * ForcePasswordChangeNew — issue #874, slice S3, forced password change
 * redrawn to `ForcePasswordChange.dc.html` (design-baseline-round-5).
 * Lives behind `newUi("signin")` (`components/ProtectedRoute.jsx`, reusing
 * S1/S2's flag — this slice adds no new one); `ForcePasswordChange.jsx`,
 * the shipped screen, is untouched and keeps rendering with the flag off.
 *
 * NOT A ROUTE, same as the shipped screen — `ProtectedRoute` renders this
 * IN PLACE of whatever the session asked for (D53), which is what makes it
 * unskippable and what makes the deep link survive: the address bar never
 * changes, so the page underneath renders the moment the flag (must_change_
 * password) lifts. See `ProtectedRoute.jsx`'s own header for the full
 * argument; nothing about that mechanism changes here.
 *
 * PRESENTATION ONLY — `changePassword`/`logout` from `useAuth()`, the
 * client-side mismatch/same-as-given checks, `claimSessionTransition` (the
 * #396 sign-out race fix), and every error STRING are reused EXACTLY as
 * `ForcePasswordChange.jsx` has them: "The two new passwords don't match.",
 * "Choose a password different from the one you were given.", the sign-out
 * failure text from `lib/signOut.js`, and the server's own words on refusal
 * (`result.error`) all already match the drawing byte-for-byte — the
 * shipped screen was already this close to what round 5 draws, so this
 * redraw is a restyle far more than a rewrite. No behaviour here calls a
 * different endpoint or widens what the server accepts.
 *
 * D48/D55/D57 — this screen already had no toast on failure (the shipped
 * `force-password-error` inline message, D48/D57's own pattern applied
 * before the ruling existed); the redraw keeps that shape and adds the D55
 * marks (TriangleAlert glyph + `--nw-alert` left rule, text stays ink).
 *
 * R-003 — "Save password" is the one gold element.
 *
 * DESIGNERS' NOTES ARE NOT COPY (S1's B1, checked again for this slice):
 * the drawing's dashed-border state captions and its trailing rationale
 * paragraphs ("Signing out is the one other thing...", "The server's own
 * words, raw: ...", "The gate lifts and the page that was asked for
 * renders...") are all checked against every state below and NONE of them
 * render. The "changed" state is drawn as precisely the absence it is —
 * `changePassword` returning `{ ok: true }` lifts the gate and
 * `ProtectedRoute` stops rendering this component at all, so there is
 * nothing to redraw for it.
 */
import { useState } from "react";
import { TriangleAlert } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { claimSessionTransition } from "../lib/sessionExpiry";
import { toast } from "sonner";
import { BrandMark } from "../components/brand/BrandMark";

/** Mirrors `PASSWORD_MIN_LENGTH` in `api/src/auth/passwordPolicy.ts` — the
 * same client-side mirror the shipped screen keeps. */
const PASSWORD_MIN_LENGTH = 10;

export default function ForcePasswordChangeNew() {
  const { user, changePassword, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // #1688: false only for a session opened by an emailed sign-in link on a
  // must_change_password account. Anything else (including a missing field)
  // keeps asking for the password the person was given. With the field
  // hidden, `currentPassword` stays "" and `changePassword` omits it.
  const needsCurrent = user?.password_change_needs_current !== false;

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("The two new passwords don't match.");
      return;
    }
    if (newPassword === currentPassword) {
      setError("Choose a password different from the one you were given.");
      return;
    }
    setError("");
    setBusy(true);
    const result = await changePassword(currentPassword, newPassword);
    setBusy(false);
    if (result.ok) {
      toast.success("Password changed — you're all set.");
      return; // context now has must_change_password: false; the gate lifts.
    }
    setError(result.error);
  };

  // Issue #326/#396, reused verbatim — see ForcePasswordChange.jsx's own
  // header for the full race explanation. Claimed BEFORE the request,
  // released on failure.
  const doSignOut = async () => {
    setError("");
    const releaseSessionTransition = claimSessionTransition();
    const result = await logout();
    if (result?.ok === false) {
      releaseSessionTransition();
      setError(result.error);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--nw-page)" }}>
      {/* Round 5 conformance (token-check) — declares its own ink token
          rather than inheriting the shell's `body` colour, same rule
          every other redrawn screen's own marker states. */}
      <span data-testid="force-password-change-new-screen" className="sr-only" style={{ color: "var(--nw-ink)" }} aria-hidden="true" />

      <div
        data-testid="force-password-change-page"
        style={{
          maxWidth: 480,
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: "var(--nw-space-stack)",
          padding: "var(--nw-space-page)",
          color: "var(--nw-ink)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--nw-ink)" }}>
          <BrandMark size={24} />
          <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 16, color: "var(--nw-navy)" }}>
            Compass
          </span>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div className="nw-title">Choose your own password</div>
          <div className="nw-lede">
            Your account was set up for you, so the password you signed in with is one someone else has
            seen. Pick your own to finish setting up — it's the last step.
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="force-password-change-form"
          style={{ display: "flex", flexDirection: "column", gap: "var(--nw-space-stack)", color: "var(--nw-ink)" }}
        >
          {needsCurrent ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div className="nw-label">The password you were given</div>
              <input
                data-testid="force-current-password"
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                className="nw-kit-input"
                style={{
                  width: "100%",
                  height: "var(--nw-size-control)",
                  borderRadius: "var(--nw-radius-control)",
                  border: "1px solid var(--nw-line)",
                  background: "var(--nw-surface)",
                  padding: "0 12px",
                  fontSize: 15,
                  color: "var(--nw-ink)",
                }}
              />
            </div>
          ) : (
            <div className="nw-help" data-testid="force-link-session-note">
              You signed in with an emailed link, so you don't need the password you were given.
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div className="nw-label">New password</div>
            <input
              data-testid="force-new-password"
              type="password"
              autoComplete="new-password"
              minLength={PASSWORD_MIN_LENGTH}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              className="nw-kit-input"
              style={{
                width: "100%",
                height: "var(--nw-size-control)",
                borderRadius: "var(--nw-radius-control)",
                border: "1px solid var(--nw-line)",
                background: "var(--nw-surface)",
                padding: "0 12px",
                fontSize: 15,
                color: "var(--nw-ink)",
              }}
            />
            <div className="nw-help">At least {PASSWORD_MIN_LENGTH} characters.</div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div className="nw-label">Confirm new password</div>
            <input
              data-testid="force-confirm-password"
              type="password"
              autoComplete="new-password"
              minLength={PASSWORD_MIN_LENGTH}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="nw-kit-input"
              style={{
                width: "100%",
                height: "var(--nw-size-control)",
                borderRadius: "var(--nw-radius-control)",
                border: "1px solid var(--nw-line)",
                background: "var(--nw-surface)",
                padding: "0 12px",
                fontSize: 15,
                color: "var(--nw-ink)",
              }}
            />
          </div>

          {/* D55 marks — every drawn failure (mismatch, same-as-given,
              server refusal, sign-out failure) renders through this ONE
              banner, exactly as the shipped screen's single `error` state
              already does. */}
          {error && (
            <div
              role="alert"
              data-testid="force-password-error"
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "var(--nw-space-inline)",
                padding: "10px 12px",
                background: "var(--nw-line-faint)",
                borderLeft: "3px solid var(--nw-alert)",
                borderRadius: "var(--nw-radius-control)",
              }}
            >
              <TriangleAlert size={16} style={{ color: "var(--nw-alert)", flexShrink: 0, marginTop: 1 }} aria-hidden="true" />
              <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)" }}>{error}</div>
            </div>
          )}

          <button
            type="submit"
            data-testid="force-password-submit"
            disabled={busy}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: busy ? 10 : 0,
              minHeight: "var(--nw-size-action)",
              background: "var(--nw-gold)",
              color: "var(--nw-ink)",
              fontWeight: 700,
              fontSize: 16,
              border: "none",
              borderRadius: "var(--nw-radius-control)",
              cursor: busy ? "not-allowed" : "pointer",
            }}
          >
            {busy && (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--nw-ink)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="animate-spin" aria-hidden="true">
                <path d="M12 3a9 9 0 1 0 9 9" />
              </svg>
            )}
            {busy ? "Saving…" : "Save password"}
          </button>
        </form>

        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {user?.email ? <div className="nw-help">Signed in as {user.email}</div> : null}
          <button
            type="button"
            onClick={doSignOut}
            data-testid="force-password-signout"
            style={{
              alignSelf: "flex-start",
              fontSize: 13,
              fontWeight: 600,
              color: "var(--nw-ink-muted)",
              textDecoration: "underline",
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
          >
            Sign out instead
          </button>
        </div>
      </div>
    </div>
  );
}
